import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const NAVY   = '#0D2B5E';
const ORANGE = '#6DC234';
const API_URL = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

const Login = ({ onSwitchToRegister, onSuccess }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    const result = await login(formData.email, formData.password);
    setLoading(false);
    if (result.success) onSuccess();
    else setError(result.error);
  };

  const handleForgotPassword = () => {
    const email = formData.email;
    if (!email) { setError('Enter your email address first'); return; }
    fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
      .then(r => r.json())
      .then(d => alert(d.message || 'Check your email for reset instructions'))
      .catch(() => alert('Something went wrong — try again'));
  };

  const fieldStyle = (name) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '13px 16px 13px 44px',
    borderRadius: '10px',
    border: `2px solid ${focusedField === name ? ORANGE : '#E2E8F0'}`,
    fontSize: '15px',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    color: NAVY,
    transition: 'border-color 0.2s',
    background: '#FAFAFA',
  });

  const iconStyle = {
    position: 'absolute', left: '14px', top: '50%',
    transform: 'translateY(-50%)', color: '#94A3B8',
    display: 'flex', alignItems: 'center',
    pointerEvents: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#F4F6F9',
      fontFamily: 'Inter, sans-serif',
    }}>
      {/* Left panel — brand */}
      <div style={{
        display: 'none',
        flex: 1,
        background: `linear-gradient(150deg, ${NAVY} 0%, #0F1E36 100%)`,
        padding: '60px 48px',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }} className="login-left">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/logo.png" alt="JayChris EDI" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
          <span style={{ color: 'white', fontWeight: '700', fontSize: '16px' }}>JayChris EDI</span>
        </div>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px', fontFamily: 'JetBrains Mono, monospace' }}>
            Trusted by brands selling to
          </div>
          {['Walmart', 'Target', 'Kohl\'s', 'Amazon', 'Costco'].map(r => (
            <div key={r} style={{ color: 'rgba(255,255,255,0.35)', fontSize: '14px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill={ORANGE}><circle cx="12" cy="12" r="10"/></svg>
              {r}
            </div>
          ))}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>
          © 2026 JayChris Management
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}>
        <div style={{ width: '100%', maxWidth: '380px' }}>
          {/* Logo mark (mobile) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px' }}>
            <img src="/logo.png" alt="JayChris EDI" style={{ width: '44px', height: '44px', objectFit: 'contain' }} />
            <div>
              <div style={{ fontWeight: '800', fontSize: '15px', color: NAVY, lineHeight: 1.2 }}>JayChris EDI</div>
              <div style={{ fontSize: '11px', color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>Retail compliance platform</div>
            </div>
          </div>

          <h1 style={{ fontSize: '26px', fontWeight: '800', color: NAVY, marginBottom: '6px', letterSpacing: '-0.5px' }}>
            Welcome back
          </h1>
          <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '32px' }}>
            Sign in to your account to continue
          </p>

          {error && (
            <div style={{
              padding: '12px 16px', borderRadius: '10px',
              background: '#FEF2F2', border: '1px solid #FECACA',
              color: '#DC2626', fontSize: '14px', fontWeight: '500',
              marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'flex-start',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '6px' }}>
                Email address
              </label>
              <div style={{ position: 'relative' }}>
                <span style={iconStyle}><MailIcon /></span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="you@company.com"
                  required
                  style={fieldStyle('email')}
                />
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: NAVY, marginBottom: '6px' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <span style={iconStyle}><LockIcon /></span>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="••••••••"
                  required
                  style={fieldStyle('password')}
                />
              </div>
            </div>

            <div style={{ textAlign: 'right', marginBottom: '28px' }}>
              <button
                type="button"
                onClick={handleForgotPassword}
                style={{ background: 'none', border: 'none', color: ORANGE, fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '14px',
                borderRadius: '10px', border: 'none',
                background: loading ? '#94A3B8' : `linear-gradient(135deg, ${ORANGE}, #5AAD28)`,
                color: 'white',
                fontSize: '15px', fontWeight: '700',
                fontFamily: 'Inter, sans-serif',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : `0 4px 14px rgba(109,194,52,0.4)`,
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 20px rgba(109,194,52,0.5)`; } }}
              onMouseLeave={e => { if (!loading) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 4px 14px rgba(109,194,52,0.4)`; } }}
            >
              {loading ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                  Signing in…
                </>
              ) : 'Sign in →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#64748B' }}>
            Don't have an account?{' '}
            <button
              onClick={onSwitchToRegister}
              style={{ background: 'none', border: 'none', color: ORANGE, fontWeight: '700', cursor: 'pointer', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}
            >
              Start free trial
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 768px) { .login-left { display: flex !important; } }
      `}</style>
    </div>
  );
};

export default Login;
